import type { ApiInputToken, MarketForRewards, MarketRewards } from '@morpho-org/dapp-core'
import type { InferContractRouterOutputs } from '@orpc/contract'

import { MarketParams } from '@morpho-org/blue-sdk'
import { ZERO_ADDRESS } from '@morpho-org/morpho-ts'

import type { Contract } from '@/lib/orpc/contracts'

type Outputs = InferContractRouterOutputs<Contract>['market']
type BorrowDialog = Outputs['borrowDialog']
type LendWithdrawDialog = Outputs['lendWithdrawDialog']
type ActionMarket = LendWithdrawDialog['market']
type ActionToken = ActionMarket['loanToken']
type ActionApySide = LendWithdrawDialog['supply']

/**
 * What the Position Manager market dialogs read, built from `market.borrowDialog` or
 * `market.lendWithdrawDialog`. Tokens keep the `ApiInputToken` form `parseApiAsset` reads, with the
 * price left null when Core has none, and `marketForRewards` the form the summary cards and review
 * dialogs read their reward rows from.
 */
export interface MarketDialogData {
  loanAsset: ApiInputToken
  collateralAsset: ApiInputToken | null
  marketParams: MarketParams
  /** The side's 6h average native APY, null when Core's snapshots do not cover the six hours. */
  avgApy: number | null
  marketForRewards: MarketForRewards
}

const toApiInputToken = (chainId: number, token: ActionToken): ApiInputToken => ({
  address: token.address,
  chain: { id: chainId },
  name: token.name ?? token.symbol ?? undefined,
  symbol: token.symbol ?? undefined,
  decimals: token.decimals,
  logoURI: token.logoUri,
  priceUsd: token.priceUsd
})

// Reward rows only render symbol, logo and APR, so decimals is a placeholder.
const toMarketRewards = (side: ActionApySide, aprKey: 'supplyApr' | 'borrowApr'): MarketRewards[] =>
  side.rewards.map(({ token, apr }) => ({
    [aprKey]: apr,
    asset: {
      address: token.address,
      chain: { id: token.chainId },
      name: token.symbol ?? undefined,
      symbol: token.symbol ?? undefined,
      decimals: 18,
      logoURI: token.logoUri
    }
  }))

function toMarketDialogData(
  chainId: number,
  market: ActionMarket,
  side: ActionApySide,
  aprKey: 'supplyApr' | 'borrowApr',
  collateralVault: BorrowDialog['collateralVault'] = null
): MarketDialogData {
  const loanAsset = toApiInputToken(chainId, market.loanToken)
  const collateralAsset = market.collateralToken && {
    ...toApiInputToken(chainId, market.collateralToken),
    vault: collateralVault && { address: collateralVault.address, name: collateralVault.name }
  }
  return {
    loanAsset,
    collateralAsset,
    marketParams: new MarketParams({
      loanToken: market.loanToken.address,
      collateralToken: market.collateralToken?.address ?? ZERO_ADDRESS,
      oracle: market.oracleAddress,
      irm: market.irmAddress,
      lltv: BigInt(market.lltv)
    }),
    avgApy: side.avgApy,
    marketForRewards: { loanAsset, state: { rewards: toMarketRewards(side, aprKey) } }
  }
}

/** The borrow-more dialog's view, with the collateral's Vault V1 backlink on its token. */
export const toBorrowDialogData = (chainId: number, data: BorrowDialog) =>
  toMarketDialogData(chainId, data.market, data.borrow, 'borrowApr', data.collateralVault)

/** The lend and lend-withdraw dialogs' view. */
export const toLendWithdrawDialogData = (chainId: number, data: LendWithdrawDialog) =>
  toMarketDialogData(chainId, data.market, data.supply, 'supplyApr')
